import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { ChevronRight, BookOpen, PenTool } from 'lucide-react';
import { toast } from 'sonner';
import BetaFeedbackPanel from './BetaFeedbackPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_SPRING_FEASTS = [
  { name: 'Passover', hebrew: 'Pesach', ref: 'Exodus 12', fulfilled: 'Jesus crucified on the exact day of Passover — the Lamb of God slain.', status: 'fulfilled' },
  { name: 'Unleavened Bread', hebrew: 'Chag HaMatzot', ref: 'Leviticus 23:6–14', fulfilled: 'Jesus buried, His body did not see corruption — the sinless bread.', status: 'fulfilled' },
  { name: 'Firstfruits', hebrew: 'Bikkurim', ref: 'Leviticus 23:9–14', fulfilled: 'Jesus rose on the exact day of Firstfruits — the first of those who sleep.', status: 'fulfilled' },
  { name: 'Pentecost', hebrew: 'Shavuot', ref: 'Leviticus 23:15–22', fulfilled: 'The Holy Spirit fell on the exact day of Pentecost — 50 days after Firstfruits.', status: 'fulfilled' },
];

const DEFAULT_FALL_FEASTS = [
  { name: 'Trumpets', hebrew: 'Yom Teruah', ref: 'Leviticus 23:23–25', description: 'A day of shouting and trumpet blasts. The last trump. The gathering.', status: 'unfulfilled' },
  { name: 'Atonement', hebrew: 'Yom Kippur', ref: 'Leviticus 23:26–32', description: 'The day of covering. National repentance. Israel looks upon the one they pierced.', status: 'unfulfilled' },
  { name: 'Tabernacles', hebrew: 'Sukkot', ref: 'Leviticus 23:33–44', description: 'God dwells with His people. The Millennial Kingdom. Emmanuel.', status: 'unfulfilled' },
];

export default function BlueprintsLesson() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const theme = useTheme();
  const [completing, setCompleting] = useState(false);
  const isAdmin = user?.email === 'cfenison@gmail.com' || user?.email === 'testadmin@test.com';

  // Editable content from API (with hardcoded fallbacks)
  const [content, setContent] = useState({});
  const [springFeasts, setSpringFeasts] = useState(DEFAULT_SPRING_FEASTS);
  const [fallFeasts, setFallFeasts] = useState(DEFAULT_FALL_FEASTS);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/lesson-content/BLUEPRINTS`);
        if (res.ok) {
          const data = await res.json();
          if (data.content && Object.keys(data.content).length > 0) setContent(data.content);
          if (data.feasts?.spring_feasts?.length > 0) setSpringFeasts(data.feasts.spring_feasts);
          if (data.feasts?.fall_feasts?.length > 0) setFallFeasts(data.feasts.fall_feasts);
        }
      } catch { /* use defaults */ }
    })();
  }, []);

  // Helper to get content with fallback
  const c = (key, fallback) => content[key] || fallback;

  const handleComplete = useCallback(async () => {
    if (!token && !user) {
      toast.info('Sign in to save your progress and continue');
      sessionStorage.setItem('pendingLessonCompletion', JSON.stringify({ moduleKey: 'BLUEPRINTS', nextRoute: '/lesson/recapitulation' }));
      navigate('/login', { state: { from: { pathname: '/lesson/blueprints' } } });
      return;
    }
    setCompleting(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${API_URL}/api/learning/progress/BLUEPRINTS/start`, { method: 'POST', headers, credentials: 'include' });
      const res = await fetch(`${API_URL}/api/learning/progress/BLUEPRINTS/complete`, { method: 'POST', headers, credentials: 'include' });
      if (res.ok) {
        toast.success('Blueprints complete! Taking you to Recapitulation...');
        setTimeout(() => navigate('/lesson/recapitulation'), 1500);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Could not complete module');
      }
    } catch { toast.error('Error completing module'); }
    setCompleting(false);
  }, [token, user, navigate]);

  // Auto-complete if returning from login with a pending action
  useEffect(() => {
    const pending = sessionStorage.getItem('pendingLessonCompletion');
    if (pending && (token || user)) {
      try {
        const { moduleKey, nextRoute } = JSON.parse(pending);
        if (moduleKey === 'BLUEPRINTS') {
          sessionStorage.removeItem('pendingLessonCompletion');
          (async () => {
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;
            await fetch(`${API_URL}/api/learning/progress/BLUEPRINTS/start`, { method: 'POST', headers, credentials: 'include' });
            const res = await fetch(`${API_URL}/api/learning/progress/BLUEPRINTS/complete`, { method: 'POST', headers, credentials: 'include' });
            if (res.ok) {
              toast.success('Blueprints complete! Taking you to Recapitulation...');
              setTimeout(() => navigate(nextRoute), 1500);
            }
          })();
        }
      } catch {}
    }
  }, [token, user, navigate]);

  return (
    <div className="min-h-screen" style={{ background: theme.bgPrimary, fontFamily: "'Times New Roman', 'Georgia', serif" }}>
      <nav className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: theme.borderPrimary }}>
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm transition-colors" style={{ color: theme.navText }} data-testid="back-to-map">
          <ChevronRight className="w-4 h-4 rotate-180" /> Back to the Map
        </button>
        <span className="text-xs tracking-widest uppercase" style={{ color: theme.textMuted }}>Legend Key 2</span>
        {isAdmin ? (
          <button onClick={() => navigate('/admin/lesson-content/BLUEPRINTS')} className="flex items-center gap-1.5 text-xs" style={{ color: theme.accentPrimary }} data-testid="btn-edit-content">
            <PenTool className="w-3.5 h-3.5" /> Edit Content
          </button>
        ) : <div className="w-20" />}
      </nav>

      <div className="max-w-3xl mx-auto px-5 py-12">

        {/* Title */}
        <div className="text-center mb-12" data-testid="lesson-header">
          <p className="text-xs tracking-[0.5em] uppercase mb-6" style={{ color: theme.labelColor }}>THE BLUEPRINTS — GOD'S DIVINE REHEARSALS</p>
          <p className="text-sm italic max-w-xl mx-auto leading-relaxed" style={{ color: theme.textSecondary }}>
            The Mo'edim — the completed reference image
          </p>
        </div>

        {/* Opening Teaching */}
        <div className="mb-12" data-testid="opening-teaching">
          <p className="text-base leading-relaxed mb-6" style={{ color: theme.textPrimary }}
            dangerouslySetInnerHTML={{ __html: c('opening_p1', 'Before Jesus walked the earth, God gave His people seven holy days — <em>Mo\'edim</em>, divine appointments — and commanded them to rehearse those days for centuries. Each one was a blueprint. When Jesus came, He fulfilled the first four of those feasts on the <em>exact day</em> they had been rehearsed for centuries. Not approximately. <strong style="color:' + theme.accentPrimary + '">Exact.</strong>') }} />
          <p className="text-base leading-relaxed" style={{ color: theme.textPrimary }}>
            {c('opening_p2', 'And the last three are still on the calendar.')}
          </p>
        </div>

        {/* Spring Feasts — Fulfilled */}
        <div className="mb-12" data-testid="spring-feasts">
          <h2 className="text-lg font-bold mb-6 tracking-wide" style={{ color: theme.labelColor }}>{c('spring_title', 'THE SPRING FEASTS — FULFILLED')}</h2>
          <p className="text-sm mb-6 italic" style={{ color: theme.textSecondary }}>
            {c('spring_subtitle', 'Four rehearsals. Four fulfillments. On the exact day each was appointed.')}
          </p>
          <div className="space-y-4">
            {springFeasts.map((feast, i) => (
              <div key={i} className="rounded-xl border p-5" data-testid={`feast-spring-${i}`}
                style={{ background: 'rgba(16,185,129,0.03)', borderColor: 'rgba(16,185,129,0.15)' }}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-bold" style={{ color: '#10b981' }}>{feast.name}</p>
                    <p className="text-xs italic" style={{ color: theme.textMuted }}>{feast.hebrew}</p>
                  </div>
                  <span className="text-[9px] tracking-wider px-2 py-0.5 rounded-full" style={{ background: 'rgba(16,185,129,0.1)', color: '#34d399', border: '1px solid rgba(16,185,129,0.2)' }}>FULFILLED</span>
                </div>
                <p className="text-xs mb-2" style={{ color: theme.textSecondary }}>{feast.ref}</p>
                <p className="text-sm leading-relaxed" style={{ color: theme.textSecondary }}>{feast.fulfilled}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Fall Feasts — Unfulfilled */}
        <div className="mb-12" data-testid="fall-feasts">
          <h2 className="text-lg font-bold mb-6 tracking-wide" style={{ color: theme.labelColor }}>{c('fall_title', 'THE FALL FEASTS — STILL ON THE CALENDAR')}</h2>
          <p className="text-sm mb-6 italic" style={{ color: theme.textSecondary }}>
            {c('fall_subtitle', 'Three appointments remain. The blueprint is still running.')}
          </p>
          <div className="space-y-4">
            {fallFeasts.map((feast, i) => (
              <div key={i} className="rounded-xl border p-5" data-testid={`feast-fall-${i}`}
                style={{ background: 'rgba(212,165,74,0.03)', borderColor: 'rgba(212,165,74,0.15)' }}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-bold" style={{ color: theme.accentPrimary }}>{feast.name}</p>
                    <p className="text-xs italic" style={{ color: theme.textMuted }}>{feast.hebrew}</p>
                  </div>
                  <span className="text-[9px] tracking-wider px-2 py-0.5 rounded-full" style={{ background: 'rgba(212,165,74,0.08)', color: theme.accentPrimary, border: '1px solid rgba(212,165,74,0.2)' }}>UNFULFILLED</span>
                </div>
                <p className="text-xs mb-2" style={{ color: theme.textSecondary }}>{feast.ref}</p>
                <p className="text-sm leading-relaxed" style={{ color: theme.textSecondary }}>{feast.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* The Pattern Callout */}
        <div className="mb-12 rounded-xl p-6 border" style={{ background: theme.accentBg, borderColor: theme.accentBorder }} data-testid="pattern-callout">
          <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: theme.labelColor }}>THE PATTERN</p>
          <p className="text-base leading-relaxed" style={{ color: theme.accentPrimary }}>
            {c('pattern_text', 'If God fulfilled the first four feasts on the exact appointed day — to the letter, to the hour — then the remaining three feasts are not metaphors. They are not suggestions. They are appointments. And they are still on the calendar. This is the box top of the puzzle. This is what the completed picture looks like.')}
          </p>
        </div>

        {/* GIT Application */}
        <div className="mb-12" data-testid="git-application">
          <h2 className="text-lg font-bold mb-4 tracking-wide" style={{ color: theme.labelColor }}>APPLY THE GIT</h2>
          <div className="rounded-xl border p-5" style={{ background: theme.scriptureBg, borderColor: theme.scriptureBorder }}>
            <p className="text-sm leading-relaxed mb-3" style={{ color: theme.textSecondary }}>
              <strong style={{ color: theme.accentPrimary }}>WHO?</strong> — {c('git_who', 'God, commanding His people through Moses.')}
            </p>
            <p className="text-sm leading-relaxed mb-3" style={{ color: theme.textSecondary }}>
              <strong style={{ color: theme.accentPrimary }}>WHAT?</strong> — {c('git_what', 'Seven appointed times to be rehearsed "forever" — fulfilled by Messiah on the exact day.')}
            </p>
            <p className="text-sm leading-relaxed" style={{ color: theme.textSecondary }}>
              <strong style={{ color: theme.accentPrimary }}>WHEN?</strong> — {c('git_when', 'Four complete. Three remaining. The Feast of Trumpets — the "last trump" — is next on the calendar.')}
            </p>
          </div>
        </div>

        {/* Beta Feedback */}
        <BetaFeedbackPanel moduleKey="BLUEPRINTS" />

        {/* Bridge CTA */}
        <div className="text-center py-8" data-testid="bridge-cta">
          <div className="h-px mx-auto mb-8" style={{ maxWidth: '200px', background: 'linear-gradient(90deg, transparent, rgba(212,165,74,0.4), transparent)' }} />
          <p className="text-base mb-6 italic" style={{ color: theme.textSecondary }}>
            {c('bridge_text', 'You have the blueprint. You know what God has already done and what remains. Now there\'s one more key before we open the corners.')}
          </p>
          <p className="text-lg font-bold mb-8" style={{ color: theme.accentPrimary }}>
            {c('bridge_question', 'How is the book of Revelation actually written?')}
          </p>
          <button onClick={handleComplete} disabled={completing}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-xl text-base font-bold transition-all border"
            style={{ background: `linear-gradient(135deg, ${theme.accentPrimary}18, ${theme.accentSecondary}18)`, borderColor: `${theme.accentPrimary}66`, color: theme.accentPrimary }}
            data-testid="btn-complete-blueprints">
            <BookOpen className="w-5 h-5" />
            {completing ? 'Marking Complete...' : 'I Have the Blueprint — Continue to Recapitulation'}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
